import React, { useState, useEffect } from "react";
import { Globe, ArrowRight, CornerDownLeft, Sparkles, Navigation, RotateCw, ShieldAlert, ArrowLeft } from "lucide-react";
import { Scraper } from "../types";

interface ScraperViewProps {
  scrapers: Scraper[];
  onPresetClick: (scraperId: string) => void;
}

export default function ScraperView({ scrapers, onPresetClick }: ScraperViewProps) {
  const [inputUrl, setInputUrl] = useState("");
  const [activeUrl, setActiveUrl] = useState("");
  const [iframeKey, setIframeKey] = useState(0); // For reloading
  const [isLoading, setIsLoading] = useState(false);

  // Default initial recommendation
  useEffect(() => {
    if (scrapers.length > 0 && !activeUrl) {
      // Find the most popular preset or default to first one
      const sorted = [...scrapers].sort((a, b) => b.clicks - a.clicks);
      setInputUrl(sorted[0].url);
    }
  }, [scrapers, activeUrl]);

  const handleScrapeSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputUrl.trim()) return;

    let targetUrl = inputUrl.trim();
    // Normalize url
    if (!targetUrl.startsWith("http://") && !targetUrl.startsWith("https://")) {
      targetUrl = "https://" + targetUrl;
    }

    setIsLoading(true);
    setActiveUrl(targetUrl);
    setInputUrl(targetUrl);
  };

  const handleSpeedDial = (preset: Scraper) => {
    onPresetClick(preset.id);
    setIsLoading(true);
    setActiveUrl(preset.url);
    setInputUrl(preset.url);
  };

  const handleReload = () => {
    if (activeUrl) {
      setIframeKey((prev) => prev + 1);
    }
  };

  const handleIframeLoad = () => {
    setIsLoading(false);
  };

  const proxyUrl = activeUrl
    ? `/api/proxy?url=${encodeURIComponent(activeUrl)}`
    : "";

  return (
    <div id="scraper-dashboard" className="space-y-6">
      
      {/* Informative Header Banner */}
      <div className="bg-gradient-to-r from-slate-900 to-indigo-950 p-6 rounded-2xl border border-slate-800 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="space-y-1">
          <h2 className="text-xl sm:text-2xl font-black text-slate-100 flex items-center gap-2 font-sans uppercase">
            <Globe className="w-6 h-6 text-indigo-400 animate-spin-slow" />
            Interactive Web Scraper Proxy
          </h2>
          <p className="text-xs sm:text-sm text-indigo-200 leading-relaxed max-w-2xl">
            Sparsely loaded iframe headers bypass CORS constraints natively. Enter *any* URL (like Wikipedia, tech blogs, or news channels) to scrape, translate, and view its live content directly within our portal.
          </p>
        </div>
      </div>

      {/* Speed Dial Presets Grid */}
      <div className="space-y-2">
        <h3 className="text-xs font-black uppercase text-slate-400 tracking-widest font-mono flex items-center gap-1.5">
          <Sparkles className="w-3.5 h-3.5 text-rose-500" />
          Recommended Web Streams
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {scrapers.map((preset) => (
            <button
              id={`preset-btn-${preset.id}`}
              key={preset.id}
              onClick={() => handleSpeedDial(preset)}
              className={`p-3 text-left rounded-xl bg-slate-900 border border-slate-800 hover:border-indigo-500/50 hover:bg-slate-900/90 hover:shadow-lg transition-all duration-300 group cursor-pointer ${
                activeUrl === preset.url ? "ring-2 ring-indigo-500/60" : ""
              }`}
            >
              <div className="text-xs font-bold text-slate-200 group-hover:text-indigo-400 line-clamp-1">
                {preset.name}
              </div>
              <div className="text-[10px] text-slate-500 font-mono truncate mt-0.5">
                {preset.url.replace(/^https?:\/\//i, "")}
              </div>
              <div className="text-[9px] bg-slate-950 px-1.5 py-0.5 rounded text-indigo-400 font-mono inline-block mt-2">
                {preset.clicks} connections
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Customized Browser Window Stage */}
      <div className="bg-slate-950 border border-slate-800 rounded-2xl overflow-hidden shadow-2xl flex flex-col min-h-[600px] lg:min-h-[750px]">
        
        {/* Browser Top Navigation Bar */}
        <div className="bg-slate-900 px-4 py-3 border-b border-slate-800 flex items-center gap-3">
          
          {/* Controls icons */}
          <div className="hidden sm:flex items-center gap-1.5 mr-2">
            <span className="w-3 h-3 rounded-full bg-rose-500/80"></span>
            <span className="w-3 h-3 rounded-full bg-amber-500/80"></span>
            <span className="w-3 h-3 rounded-full bg-emerald-500/80"></span>
          </div>

          <button
            onClick={() => { setActiveUrl(""); setInputUrl(""); }}
            className="p-1.5 rounded-md hover:bg-slate-800 text-slate-400 hover:text-white transition-colors"
            title="Browser Home"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>

          <button
            onClick={handleReload}
            disabled={!activeUrl}
            className={`p-1.5 rounded-md hover:bg-slate-800 text-slate-400 hover:text-white transition-colors ${
              !activeUrl ? "opacity-30 cursor-not-allowed" : "cursor-pointer"
            }`}
            title="Reload Page"
          >
            <RotateCw className={`w-4 h-4 ${isLoading ? "animate-spin text-indigo-400" : ""}`} />
          </button>

          {/* Browser Address Bar Input */}
          <form onSubmit={handleScrapeSubmit} className="flex-1 flex items-center gap-2">
            <div className="relative flex-1">
              <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-slate-500">
                <Globe className="w-4 h-4 text-slate-400" />
              </span>
              <input
                id="scraper-address-bar"
                type="text"
                placeholder="Enter any website URL (e.g. www.wikipedia.org)..."
                value={inputUrl}
                onChange={(e) => setInputUrl(e.target.value)}
                className="w-full bg-slate-950 border border-slate-800 rounded-lg pl-9 pr-4 py-2 text-xs sm:text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-indigo-500/60 font-sans tracking-wide"
              />
            </div>
            <button
              id="scraper-go-btn"
              type="submit"
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-xs sm:text-sm font-bold flex items-center gap-1 transition-colors cursor-pointer"
            >
              Go <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </form>
        </div>

        {/* Viewport Frame */}
        <div className="relative flex-1 bg-slate-900 flex flex-col justify-stretch">
          
          {/* Iframe element */}
          {proxyUrl ? (
            <>
              {isLoading && (
                <div className="absolute inset-0 bg-slate-950/80 z-10 flex flex-col items-center justify-center gap-3">
                  <div className="w-10 h-10 border-4 border-t-indigo-500 border-indigo-950 rounded-full animate-spin"></div>
                  <span className="text-xs text-indigo-300 font-mono tracking-widest animate-pulse">
                    SCRAPING & CONVERTING DATA...
                  </span>
                </div>
              )}
              <iframe
                id="scraper-iframe-viewport"
                key={iframeKey}
                src={proxyUrl}
                onLoad={handleIframeLoad}
                className="w-full flex-1 bg-white border-0 min-h-[550px] lg:min-h-[700px]"
                sandbox="allow-same-origin allow-scripts allow-forms"
                referrerPolicy="no-referrer"
              />
            </>
          ) : (
            /* Empty State Prompt */
            <div className="flex-1 flex flex-col items-center justify-center py-20 px-6 text-center max-w-xl mx-auto space-y-4">
              <div className="w-14 h-14 rounded-full bg-indigo-950/40 border border-indigo-500/20 flex items-center justify-center">
                <Navigation className="w-6 h-6 text-indigo-400" />
              </div>
              <div className="space-y-1">
                <h4 className="text-slate-200 font-bold text-base font-sans">
                  No Webpage Active
                </h4>
                <p className="text-xs sm:text-sm text-slate-400 leading-relaxed">
                  Enter a domain URL in the browser bar above or click one of the suggested Web Streams to load its full HTML directly.
                </p>
              </div>
              <div className="p-3.5 rounded-xl bg-slate-950 border border-slate-800 text-left text-xs font-mono space-y-1 text-indigo-300 w-full">
                <span className="font-bold text-amber-500 flex items-center gap-1 mb-1">
                  <ShieldAlert className="w-3.5 h-3.5" /> Note:
                </span>
                • Cookies and logins might be constrained by iframe policies.<br/>
                • Some sites block scraping but our proxy bypasses basic CORS tags.<br/>
                • Internal links clicked inside the iframe will continue to be proxied.
              </div>
            </div>
          )}

        </div>

      </div>

    </div>
  );
}
